import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

const TPRMTools = () => {
  const { orgId } = useParams();
  const id = useSelector(state => {
    if (orgId) {
      return orgId;
    } else {
      return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
    }
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // Function to handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is excel
      if (
        file.type === "application/vnd.ms-excel" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setSelectedFile(file);
        setUploadError("");
      } else {
        setUploadError("Please upload only Excel files (.xls or .xlsx)");
        setSelectedFile(null);
      }
    }
  };

  // Function to handle upload and process the file
  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file first");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("questioner_form_excel", selectedFile);

      const response = await axios.post(
        `https://api.brandsek.com/api/tprm/${id}/answer-policy-questions-excel`,
        formData,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Create a download link for the response
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `questionnaire_responses_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Reset after successful download
      setSelectedFile(null);
    } catch (error) {
      // console.error removed
      setUploadError("Error processing file. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container-fluid risk-dashboard">
      <div className="block-header">
        <div className="row">
          <div className="col-md-6 col-sm-12">
            <h1 className="welcome-text mb-3">TPRM AI Filled Questionnaire</h1>
          </div>
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="row">
                  <div className="col-lg-8 col-md-10 mx-auto">
                    <div className="text-center mb-4">
                      <h5>Upload your Excel file with questionnaire data to get AI-powered responses</h5>
                      <p className="text-muted">Supported formats: .xls, .xlsx</p>
                    </div>

                    <div className="form-group">
                      <label className="d-block p-4" style={{
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        marginBottom: '20px',
                        backgroundColor: '#f8f9fa',
                        transition: 'all 0.3s ease'
                      }}>
                        <input
                          type="file"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                          accept=".xls,.xlsx"
                        />
                        <svg
                          width="60"
                          height="60"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#888"
                          strokeWidth="2"
                          style={{ display: 'block', margin: '0 auto 15px' }}
                        >
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <h6 className="mb-2">
                          {selectedFile ? selectedFile.name : "Click to browse or drop Excel file here"}
                        </h6>
                        <p className="text-muted mb-0">Maximum file size: 10MB</p>
                      </label>

                      {uploadError && (
                        <div className="alert alert-danger py-2" role="alert">
                          {uploadError}
                        </div>
                      )}

                      {selectedFile && (
                        <div className="selected-file d-flex align-items-center justify-content-between p-3 bg-light rounded mb-4">
                          <span className="text-truncate" style={{ maxWidth: '300px' }}>
                            {selectedFile.name}
                          </span>
                          <button
                            className="btn btn-sm btn-light p-0"
                            style={{ maxWidth: "20px", minWidth: "20px" }}
                            onClick={() => setSelectedFile(null)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      )}

                      <div className="text-center">
                        <button
                          className="btn btn-primary"
                          onClick={handleUpload}
                          disabled={!selectedFile || isUploading}
                          style={{
                            backgroundColor: '#08283f',
                            border: 'none',
                            padding: '10px 30px',
                            borderRadius: '6px',
                            fontWeight: '500',
                            minWidth: '200px'
                          }}
                        >
                          {isUploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                              Processing...
                            </>
                          ) : (
                            "Upload & Process"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TPRMTools;
